import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Package, Plus, Trash2, CheckCircle2, ShoppingCart, Clock, BookOpen } from "lucide-react";
import { CataloguePicker } from "./CataloguePicker";

const API = `${window.location.origin}/api`;

const STATUS_META = {
  to_order: { label: "To Order", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Clock },
  ordered: { label: "Ordered", color: "bg-blue-100 text-blue-800 border-blue-200", icon: ShoppingCart },
  received: { label: "Received", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
};

const emptyRow = { name: "", quantity: 1, unit_cost: 0, supplier: "", status: "to_order", notes: "" };

export const JobMaterialsPanel = ({ authHeaders, jobId }) => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [row, setRow] = useState(emptyRow);
  const [pickerOpen, setPickerOpen] = useState(false);

  const fetchList = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/materials?job_id=${encodeURIComponent(jobId)}`, authHeaders);
      setMaterials(res.data || []);
    } catch (e) {
      toast.error("Failed to load materials");
    } finally {
      setLoading(false);
    }
  }, [authHeaders, jobId]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleAdd = async () => {
    if (!row.name.trim()) { toast.error("Name required"); return; }
    setAdding(true);
    try {
      await axios.post(`${API}/admin/materials`, {
        job_id: jobId,
        name: row.name.trim(),
        quantity: Number(row.quantity) || 0,
        unit_cost: Number(row.unit_cost) || 0,
        supplier: row.supplier,
        status: row.status,
        notes: row.notes,
      }, authHeaders);
      toast.success("Added");
      setRow(emptyRow);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    } finally {
      setAdding(false);
    }
  };

  const handleStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/admin/materials/${id}`, { status: newStatus }, authHeaders);
      fetchList();
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this material?")) return;
    try {
      await axios.delete(`${API}/admin/materials/${id}`, authHeaders);
      fetchList();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const totalCost = materials.reduce((s, m) => s + (m.total_cost || 0), 0);
  const counts = materials.reduce((a, m) => {
    const s = m.status || 'to_order';
    a[s] = (a[s] || 0) + 1;
    return a;
  }, {});

  return (
    <div className="border rounded-lg p-3 bg-muted/20" data-testid="job-materials-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 font-medium text-sm">
          <Package className="h-4 w-4" /> Materials
          {materials.length > 0 && <Badge variant="outline" className="ml-1">{materials.length}</Badge>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="h-7 text-xs px-2" onClick={() => setPickerOpen(true)} data-testid="job-material-add-from-catalogue-btn">
            <BookOpen className="h-3.5 w-3.5 mr-1" /> Catalogue
          </Button>
          <div className="text-xs text-muted-foreground">Total: £{totalCost.toFixed(2)}</div>
        </div>
      </div>

      {/* Status summary ribbon — tells field workers the shopping situation at a glance */}
      {materials.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2" data-testid="job-materials-ribbon">
          {(counts.to_order || 0) > 0 && (
            <Badge className={`${STATUS_META.to_order.color} text-[10px]`} data-testid="ribbon-to-order">
              {counts.to_order} to order
            </Badge>
          )}
          {(counts.ordered || 0) > 0 && (
            <Badge className={`${STATUS_META.ordered.color} text-[10px]`} data-testid="ribbon-ordered">
              {counts.ordered} ordered
            </Badge>
          )}
          {(counts.received || 0) > 0 && (
            <Badge className={`${STATUS_META.received.color} text-[10px]`} data-testid="ribbon-received">
              {counts.received} received
            </Badge>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-xs text-muted-foreground py-2">Loading…</div>
      ) : materials.length === 0 ? (
        <div className="text-xs text-muted-foreground py-2">No materials logged yet.</div>
      ) : (
        <div className="space-y-1 mb-3">
          {materials.map(m => {
            const meta = STATUS_META[m.status] || STATUS_META.to_order;
            return (
              <div key={m.id} className="flex items-center gap-2 text-sm bg-white rounded-md border px-2 py-1.5" data-testid={`job-material-${m.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{m.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {m.quantity} {m.unit} · £{(m.total_cost || 0).toFixed(2)}
                    {m.supplier ? ` · ${m.supplier}` : ""}
                  </div>
                </div>
                <Select value={m.status} onValueChange={(v) => handleStatus(m.id, v)}>
                  <SelectTrigger className={`h-7 w-28 text-xs ${meta.color}`} data-testid={`job-material-status-${m.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="to_order">To Order</SelectItem>
                    <SelectItem value="ordered">Ordered</SelectItem>
                    <SelectItem value="received">Received</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(m.id)} className="h-7 w-7 p-0" data-testid={`job-material-delete-${m.id}`}>
                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                </Button>
              </div>
            );
          })}
        </div>
      )}

      {/* Quick-add row */}
      <div className="grid grid-cols-6 gap-1 items-end">
        <div className="col-span-2">
          <Label className="text-xs">Name</Label>
          <Input className="h-8 text-sm" value={row.name} onChange={(e) => setRow(p => ({ ...p, name: e.target.value }))} placeholder="Material" data-testid="job-material-add-name" />
        </div>
        <div>
          <Label className="text-xs">Qty</Label>
          <Input className="h-8 text-sm" type="number" step="0.01" value={row.quantity} onChange={(e) => setRow(p => ({ ...p, quantity: e.target.value }))} data-testid="job-material-add-qty" />
        </div>
        <div>
          <Label className="text-xs">£/unit</Label>
          <Input className="h-8 text-sm" type="number" step="0.01" value={row.unit_cost} onChange={(e) => setRow(p => ({ ...p, unit_cost: e.target.value }))} data-testid="job-material-add-cost" />
        </div>
        <div>
          <Label className="text-xs">Supplier</Label>
          <Input className="h-8 text-sm" value={row.supplier} onChange={(e) => setRow(p => ({ ...p, supplier: e.target.value }))} data-testid="job-material-add-supplier" />
        </div>
        <Button size="sm" onClick={handleAdd} disabled={adding} data-testid="job-material-add-btn" className="h-8">
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>

      <CataloguePicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onAdded={() => { setPickerOpen(false); fetchList(); }}
        authHeaders={authHeaders}
        jobId={jobId}
      />
    </div>
  );
};

export default JobMaterialsPanel;
